import { useState, useRef, useCallback, useEffect } from 'react'
import { isNativePlatform } from '@/utils/platform'

/**
 * Speech-to-text hook that works on both web (Web Speech API) and
 * native iOS (Capacitor plugin).
 *
 * Returns `interimText` for real-time preview while the user speaks.
 * Calls `onTranscript` only with finalized text.
 *
 * @param {(text: string) => void} onTranscript – called with final recognized text
 * @returns {{ isListening: boolean, startListening: () => void, stopListening: () => void, isSupported: boolean, interimText: string }}
 */
export function useSpeechToText(onTranscript) {
  const [isListening, setIsListening] = useState(false)
  const [isSupported, setIsSupported] = useState(false)
  const [interimText, setInterimText] = useState('')
  const recognitionRef = useRef(null)
  const pluginRef = useRef(null)
  const listenerRef = useRef(null)
  const onTranscriptRef = useRef(onTranscript)
  onTranscriptRef.current = onTranscript

  // Detect support on mount
  useEffect(() => {
    if (isNativePlatform()) {
      import('@capgo/capacitor-speech-recognition').then(({ SpeechRecognition }) => {
        SpeechRecognition.available().then(({ available }) => {
          if (available) {
            pluginRef.current = SpeechRecognition
            setIsSupported(true)
          }
        }).catch(() => {})
      }).catch(() => {})
    } else {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      if (SpeechRecognition) {
        setIsSupported(true)
      }
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop() } catch {}
      }
      if (pluginRef.current && isNativePlatform()) {
        try { pluginRef.current.stop() } catch {}
        if (listenerRef.current) {
          try { listenerRef.current.remove() } catch {}
        }
      }
    }
  }, [])

  const startListening = useCallback(async () => {
    if (isListening) return

    if (isNativePlatform() && pluginRef.current) {
      const plugin = pluginRef.current
      try {
        const { speechRecognition } = await plugin.requestPermissions()
        if (speechRecognition !== 'granted') {
          setIsSupported(false)
          return
        }
        // Remove previous listener if any (don't use removeAllListeners to
        // avoid disrupting other Capacitor plugin listeners)
        if (listenerRef.current) {
          try { await listenerRef.current.remove() } catch {}
        }
        listenerRef.current = await plugin.addListener('partialResults', (data) => {
          if (data.matches && data.matches.length > 0) {
            const text = data.matches[0]
            // With partialResults: true, we get interim results as the user speaks.
            // Show them as interimText for immediate feedback.
            setInterimText(text)
          }
        })
        await plugin.start({ language: 'en-US', partialResults: true, popup: false })
        setIsListening(true)
      } catch {
        setIsSupported(false)
      }
    } else {
      // Web Speech API path
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      if (!SpeechRecognition) return

      const recognition = new SpeechRecognition()
      recognition.lang = 'en-US'
      recognition.continuous = true
      recognition.interimResults = true

      recognition.onresult = (event) => {
        let interim = ''
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript
          if (event.results[i].isFinal) {
            // Commit final text to the input
            onTranscriptRef.current(transcript)
            setInterimText('')
          } else {
            interim += transcript
          }
        }
        if (interim) {
          setInterimText(interim)
        }
      }

      recognition.onend = () => {
        setIsListening(false)
        setInterimText('')
        recognitionRef.current = null
      }

      recognition.onerror = (event) => {
        if (event.error === 'not-allowed') {
          setIsSupported(false)
        }
        setIsListening(false)
        setInterimText('')
        recognitionRef.current = null
      }

      recognitionRef.current = recognition
      recognition.start()
      setIsListening(true)
    }
  }, [isListening])

  const stopListening = useCallback(async () => {
    if (isNativePlatform() && pluginRef.current) {
      try {
        await pluginRef.current.stop()
      } catch {}
      // Remove only our listener, not all listeners
      if (listenerRef.current) {
        try { await listenerRef.current.remove() } catch {}
        listenerRef.current = null
      }
      // Commit whatever interim text we have as the final result
      setInterimText((current) => {
        if (current) {
          onTranscriptRef.current(current)
        }
        return ''
      })
      setIsListening(false)
    } else if (recognitionRef.current) {
      recognitionRef.current.stop()
      // onend handler will set isListening = false
    }
  }, [])

  return { isListening, startListening, stopListening, isSupported, interimText }
}
